// src/pages/dashboard/KpiPerformancePage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  // Layout
  Box, Paper, Grid, Card, CardContent, Stack, Divider,
  // Typography
  Typography,
  // Data Display
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  // Inputs
  TextField, InputAdornment, Autocomplete, ToggleButtonGroup, ToggleButton,
  // Feedback
  Alert, Snackbar, CircularProgress, Skeleton,
  // Actions
  Button, IconButton, Tooltip,
  // Dialog
  Dialog, DialogTitle, DialogContent, DialogActions,
  // Utils
  useTheme, useMediaQuery
} from '@mui/material';
import {
  // Icons
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { userAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

// ────────────────────────────────────────────────────────────────────────────
// Chart.js Registration
// ────────────────────────────────────────────────────────────────────────────
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// ────────────────────────────────────────────────────────────────────────────
// Constants & Configuration
// ────────────────────────────────────────────────────────────────────────────
const PERIOD_TYPES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const VIEW_MODES = [
  { value: 'chart', icon: <BarChartIcon fontSize="small" />, label: 'Chart' },
  { value: 'table', icon: <TableChartIcon fontSize="small" />, label: 'Table' },
  { value: 'cards', icon: <ViewModuleIcon fontSize="small" />, label: 'Cards' },
];

const COLOR_PALETTE = {
  primary: '#4361ee',
  primaryLight: 'rgba(67, 97, 238, 0.1)',
  success: '#4cc9f0',
  successLight: 'rgba(76, 201, 240, 0.1)',
  warning: '#f72585',
  warningLight: 'rgba(247, 37, 133, 0.1)',
  info: '#4895ef',
  background: '#f8fafc',
  surface: '#ffffff',
  border: '#e2e8f0',
  text: {
    primary: '#1e293b',
    secondary: '#64748b',
  },
};

const CHART_COLORS = {
  performance: {
    bg: 'rgba(67, 97, 238, 0.6)',
    border: 'rgba(67, 97, 238, 1)',
    fill: 'rgba(67, 97, 238, 0.2)',
  },
  tasks: {
    bg: 'rgba(76, 201, 240, 0.6)',
    border: 'rgba(76, 201, 240, 1)',
    fill: 'rgba(76, 201, 240, 0.2)',
  },
  onTime: {
    bg: 'rgba(16, 185, 129, 0.6)',
    border: 'rgba(16, 185, 129, 1)',
    fill: 'rgba(16, 185, 129, 0.2)',
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Helper Components
// ────────────────────────────────────────────────────────────────────────────

/**
 * Metric Card Component - Reusable summary card
 */
const MetricCard = React.memo(({ title, value, suffix = '', color = 'primary', icon, tooltip }) => {
  const theme = useTheme();
  const bgColor = {
    primary: COLOR_PALETTE.primaryLight,
    success: COLOR_PALETTE.successLight,
    warning: COLOR_PALETTE.warningLight,
    info: 'rgba(72, 149, 239, 0.1)',
  }[color] || COLOR_PALETTE.primaryLight;

  const textColor = {
    primary: COLOR_PALETTE.primary,
    success: '#10b981',
    warning: COLOR_PALETTE.warning,
    info: COLOR_PALETTE.info,
  }[color] || COLOR_PALETTE.primary;

  return (
    <Card 
      sx={{ 
        borderRadius: 2, 
        bgcolor: bgColor,
        border: `1px solid ${COLOR_PALETTE.border}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography color="text.secondary" variant="caption" fontWeight={500}>
            {title}
          </Typography>
          {icon && (
            <Tooltip title={tooltip || title}>
              <IconButton size="small" sx={{ color: textColor, '&:hover': { bgcolor: 'transparent' } }}>
                {icon}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Typography 
          variant="h3" 
          fontWeight={700} 
          sx={{ color: textColor, lineHeight: 1 }}
        >
          {value}{suffix}
        </Typography>
      </CardContent>
    </Card>
  );
});

MetricCard.displayName = 'MetricCard';

/**
 * Chart Container with loading state
 */
const ChartContainer = ({ loading, children, height = 400 }) => {
  if (loading) {
    return (
      <Paper sx={{ p: 2, borderRadius: 2, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} thickness={4} />
          <Typography color="text.secondary">Loading analytics...</Typography>
        </Stack>
      </Paper>
    );
  }
  return (
    <Paper sx={{ p: 2, borderRadius: 2, height, position: 'relative' }}>
      {children}
    </Paper>
  );
};

/**
 * Empty State Component
 */
const EmptyState = ({ message, action, icon = <AssessmentIcon sx={{ fontSize: 48, color: 'text.disabled' }} /> }) => (
  <Paper 
    sx={{ 
      p: 4, 
      borderRadius: 2, 
      textAlign: 'center', 
      bgcolor: 'background.default',
      border: `1px dashed ${COLOR_PALETTE.border}`
    }}
  >
    <Stack alignItems="center" spacing={2}>
      {icon}
      <Typography color="text.secondary">{message}</Typography>
      {action}
    </Stack>
  </Paper>
);

// ────────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────────

const KpiPerformancePage = () => {
  // ─── Auth & Permissions ──────────────────────────────────────────────────
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.teamMembership?.role?.name?.toLowerCase() === 'system administrator';
  const isClient = !!authUser?.client_id;

  // ─── Theme & Responsive ──────────────────────────────────────────────────
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ─── State ───────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState([]);
  const [periodType, setPeriodType] = useState('monthly');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [exportLoading, setExportLoading] = useState(false);

  // ─── Data Fetching ───────────────────────────────────────────────────────
  const fetchKpiData = useCallback(async () => {
    if (isClient) return;
    
    setLoading(true);
    try {
      const params = { period_type: periodType };
      if (isAdmin && selectedUser?.id) {
        params.user_id = selectedUser.id;
      }
      if (dateRange?.start) {
        params.start_date = dateRange.start;
      }
      if (dateRange?.end) {
        params.end_date = dateRange.end;
      }
      
      const res = await userAPI.getKpiReport(params);
      setKpiData(res.data?.data || []);
      
      if (!res.data?.data?.length) {
        setSnackbar({
          open: true,
          message: 'No KPI data found for selected filters',
          severity: 'info'
        });
      }
    } catch (err) {
      console.error('Failed to fetch KPI data:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to load KPI data. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [periodType, selectedUser, dateRange, isClient, isAdmin]);

  const fetchTeamMembers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await userAPI.getTeamPerformance();
      setTeamMembers(res.data?.team || []);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isClient) {
      toast.error('Clients cannot access KPI reports');
      return;
    }
    fetchKpiData();
    if (isAdmin) fetchTeamMembers();
  }, [fetchKpiData, fetchTeamMembers, isClient, isAdmin]);

  // ─── Filtering & Data Processing ─────────────────────────────────────────
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return kpiData;
    
    const query = searchQuery.toLowerCase();
    return kpiData.filter(item => {
      const period = (item.month || item.year || '').toLowerCase();
      return period.includes(query);
    });
  }, [kpiData, searchQuery]);

  const latestData = useMemo(() => filteredData[0] || null, [filteredData]);

  // ─── Chart Configuration ─────────────────────────────────────────────────
  const chartConfig = useMemo(() => {
    const labels = filteredData.map(d => d.month || d.year).reverse();
    
    return {
      bar: {
        data: {
          labels,
          datasets: [
            {
              label: 'Performance Score',
              data: filteredData.map(d => d.performance_score).reverse(),
              backgroundColor: CHART_COLORS.performance.bg,
              borderColor: CHART_COLORS.performance.border,
              borderWidth: 2,
              borderRadius: 6,
              barPercentage: 0.7,
            },
            {
              label: 'Tasks Completed',
              data: filteredData.map(d => d.tasks_completed).reverse(),
              backgroundColor: CHART_COLORS.tasks.bg,
              borderColor: CHART_COLORS.tasks.border,
              borderWidth: 2,
              borderRadius: 6,
              barPercentage: 0.7,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              position: 'top',
              labels: { 
                usePointStyle: true, 
                padding: 20,
                font: { size: 11 }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: COLOR_PALETTE.border,
              borderWidth: 1,
              padding: 12,
              displayColors: true,
              callbacks: {
                label: (context) => `${context.dataset.label}: ${context.parsed.y}`
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.04)', drawBorder: false },
              ticks: { font: { size: 10 }, color: COLOR_PALETTE.text.secondary }
            },
            x: {
              grid: { display: false, drawBorder: false },
              ticks: { 
                font: { size: 10 }, 
                color: COLOR_PALETTE.text.secondary,
                maxRotation: 45,
                minRotation: 45
              }
            }
          }
        }
      },
      line: {
        data: {
          labels,
          datasets: [
            {
              label: 'Performance Score',
              data: filteredData.map(d => d.performance_score).reverse(),
              borderColor: CHART_COLORS.performance.border,
              backgroundColor: CHART_COLORS.performance.fill,
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#fff',
              pointBorderColor: CHART_COLORS.performance.border,
              pointBorderWidth: 2,
            },
            {
              label: 'On-Time %',
              data: filteredData.map(d => d.tasks_on_time_percentage).reverse(),
              borderColor: CHART_COLORS.onTime.border,
              backgroundColor: CHART_COLORS.onTime.fill,
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#fff',
              pointBorderColor: CHART_COLORS.onTime.border,
              pointBorderWidth: 2,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              position: 'top',
              labels: { usePointStyle: true, padding: 20, font: { size: 11 } }
            },
            tooltip: {
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: COLOR_PALETTE.border,
              borderWidth: 1,
              padding: 12,
              callbacks: {
                label: (context) => {
                  const value = context.parsed.y;
                  return context.dataset.label === 'On-Time %' 
                    ? `${context.dataset.label}: ${value}%`
                    : `${context.dataset.label}: ${value}`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.04)', drawBorder: false },
              ticks: { font: { size: 10 }, color: COLOR_PALETTE.text.secondary }
            },
            x: {
              grid: { display: false, drawBorder: false },
              ticks: { 
                font: { size: 10 }, 
                color: COLOR_PALETTE.text.secondary,
                maxRotation: 45,
                minRotation: 45
              }
            }
          }
        }
      }
    };
  }, [filteredData]);

  // ─── Actions ─────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(() => {
    fetchKpiData();
    toast.info('Refreshing data...');
  }, [fetchKpiData]);

  const handleExport = useCallback(async (format = 'csv') => {
    if (!filteredData.length) {
      toast.warning('No data to export');
      return;
    }
    
    setExportLoading(true);
    try {
      // Simple CSV export - enhance with backend endpoint for large datasets
      const headers = ['Period', 'Tasks Completed', 'On-Time %', 'Projects', 'Velocity', 'Backlog', 'Score'];
      const rows = filteredData.map(d => [
        d.month || d.year,
        d.tasks_completed,
        `${d.tasks_on_time_percentage}%`,
        d.projects_completed,
        d.sprint_velocity,
        d.backlog_cleared,
        d.performance_score
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `kpi-report-${periodType}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Report exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export report');
    } finally {
      setExportLoading(false);
    }
  }, [filteredData, periodType]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedUser(null);
    setDateRange({ start: null, end: null });
    toast.info('Filters cleared');
  }, []);

  // ─── Render Helpers ──────────────────────────────────────────────────────
  const renderFilters = () => (
    <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, mb: 3 }}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={{ xs: 2, sm: 2 }} 
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        {/* Period Toggle */}
        <ToggleButtonGroup
          value={periodType}
          exclusive
          onChange={(e, val) => val && setPeriodType(val)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { px: 2 } }}
        >
          {PERIOD_TYPES.map(type => (
            <ToggleButton key={type.value} value={type.value}>
              {type.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search period..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={() => setSearchQuery('')}
                  edge="end"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ minWidth: { xs: '100%', sm: 220 } }}
        />

        {/* User Filter (Admin) */}
        {isAdmin && (
          <Autocomplete
            size="small"
            options={teamMembers}
            getOptionLabel={(opt) => opt?.name || ''}
            value={selectedUser}
            onChange={(e, val) => setSelectedUser(val)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="All team members"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
          />
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, ml: { sm: 'auto' } }}>
          <Tooltip title="Clear filters">
            <IconButton 
              size="small" 
              onClick={handleClearFilters}
              disabled={!searchQuery && !selectedUser && !dateRange?.start}
            >
              <FilterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh data">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" sx={{ animation: loading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export report">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={() => handleExport('csv')}
              disabled={exportLoading || !filteredData.length}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Export
            </Button>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  );

  const renderSummaryCards = () => {
    if (loading) {
      return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (!latestData) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <MetricCard
            title="Performance Score"
            value={latestData.performance_score}
            color="primary"
            icon={<TrendingUpIcon fontSize="small" />}
            tooltip="Overall performance rating (0-100)"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard
            title="Tasks Completed"
            value={latestData.tasks_completed}
            color="success"
            icon={<AssessmentIcon fontSize="small" />}
            tooltip="Total tasks finished this period"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard
            title="On-Time Delivery"
            value={latestData.tasks_on_time_percentage}
            suffix="%"
            color="warning"
            icon={<CalendarIcon fontSize="small" />}
            tooltip="Percentage of tasks delivered on schedule"
          />
        </Grid>
      </Grid>
    );
  };

  const renderChart = () => {
    if (loading) {
      return (
        <ChartContainer loading={true} height={isMobile ? 300 : 400} />
      );
    }

    if (!filteredData.length) {
      return (
        <ChartContainer loading={false} height={isMobile ? 300 : 400}>
          <EmptyState 
            message="No data available for selected filters"
            action={
              <Button variant="outlined" size="small" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            }
          />
        </ChartContainer>
      );
    }

    return (
      <ChartContainer loading={false} height={isMobile ? 300 : 400}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Performance Analytics
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
            aria-label="Chart view mode"
          >
            {VIEW_MODES.map(mode => (
              <ToggleButton 
                key={mode.value} 
                value={mode.value}
                aria-label={mode.label}
              >
                {mode.icon}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        
        {viewMode === 'cards' ? (
          <Grid container spacing={2}>
            {filteredData.slice(0, 6).map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Chip 
                        label={item.month || item.year} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ width: 'fit-content' }}
                      />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Score</Typography>
                        <Typography fontWeight={600} color="primary">{item.performance_score}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Tasks</Typography>
                        <Typography fontWeight={500}>{item.tasks_completed}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">On-Time</Typography>
                        <Chip 
                          label={`${item.tasks_on_time_percentage}%`} 
                          size="small"
                          color={item.tasks_on_time_percentage >= 80 ? 'success' : 'warning'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : viewMode === 'chart' ? (
          <Bar 
            key="bar-chart"
            data={chartConfig.bar.data} 
            options={chartConfig.bar.options} 
          />
        ) : (
          <Line 
            key="line-chart"
            data={chartConfig.line.data} 
            options={chartConfig.line.options} 
          />
        )}
      </ChartContainer>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Skeleton variant="rectangular" height={400} />
        </Paper>
      );
    }

    if (!filteredData.length) {
      return (
        <EmptyState 
          message="No records match your search"
          action={
            <Button variant="contained" size="small" onClick={handleClearFilters}>
              Reset Search
            </Button>
          }
          icon={<SearchIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        />
      );
    }

    return (
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: isMobile ? '60vh' : '70vh' }}>
          <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">Tasks</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">On-Time %</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">Projects</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">Velocity</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">Backlog</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, idx) => {
                const scoreColor = row.performance_score >= 80 
                  ? 'success.main' 
                  : row.performance_score >= 60 
                    ? 'primary.main' 
                    : 'warning.main';
                
                const onTimeColor = row.tasks_on_time_percentage >= 80 
                  ? 'success' 
                  : row.tasks_on_time_percentage >= 60 
                    ? 'info' 
                    : 'warning';

                return (
                  <TableRow 
                    key={row.id || idx} 
                    hover
                    sx={{ 
                      '&:last-child td': { borderBottom: 0 },
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={row.month || row.year} 
                        size={isMobile ? 'small' : 'medium'}
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right" fontWeight={500}>
                      {row.tasks_completed}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.tasks_on_time_percentage}%`}
                        size={isMobile ? 'small' : 'medium'}
                        color={onTimeColor}
                        variant={onTimeColor === 'warning' ? 'outlined' : 'filled'}
                        sx={{ 
                          fontWeight: 600,
                          fontSize: isMobile ? '0.7rem' : '0.875rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{row.projects_completed}</TableCell>
                    <TableCell align="right" color="text.secondary">
                      {row.sprint_velocity}
                    </TableCell>
                    <TableCell align="right">{row.backlog_cleared}</TableCell>
                    <TableCell align="right">
                      <Typography 
                        fontWeight={700} 
                        sx={{ color: scoreColor }}
                      >
                        {row.performance_score}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  // ─── Access Control ──────────────────────────────────────────────────────
  if (isClient) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Alert severity="error" sx={{ maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Access Restricted</Typography>
          <Typography color="text.secondary">
            KPI reports are only available to team members and administrators.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // ─── Main Render ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 }, 
      bgcolor: COLOR_PALETTE.background, 
      minHeight: '100vh' 
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 }, 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2, mt: 3 
      }}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight={700} 
            color="text.primary"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              bgcolor: COLOR_PALETTE.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AssessmentIcon sx={{ color: COLOR_PALETTE.primary, fontSize: { xs: 24, sm: 28 } }} />
            </Box>
            Performance & KPI Report
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track team productivity, delivery metrics, and performance trends
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh} 
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Refresh'}
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      {renderFilters()}

      {/* Summary Cards */}
      {renderSummaryCards()}

      {/* Analytics Chart */}
      {renderChart()}

      {/* Data Table */}
      <Box sx={{ mt: 5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Detailed Records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} {filteredData.length === 1 ? 'record' : 'records'}
          </Typography>
        </Stack>
        {renderTable()}
      </Box>

      {/* Footer Note */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          Data updates automatically. Last refreshed: {new Date().toLocaleTimeString()}
          {selectedUser && ` • Viewing: ${selectedUser.name}`}
        </Typography>
      </Box>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default React.memo(KpiPerformancePage);